import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";

const LINES = ["CAPTURING LIGHT.", "CRAFTING MOTION.", "OBSESSED WITH THE DETAILS."];
const EASE = [0.16, 1, 0.3, 1];
const BASE_DELAY = 1.9;

const Corner = ({ className }) => (
  <span className={`absolute w-6 h-6 border-white/40 ${className}`} />
);

export const Hero = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 0.25], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.22], [1, 0]);
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const [soundOn, setSoundOn] = useState(false);
  const [heroVisible, setHeroVisible] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    const section = sectionRef.current;
    if (!video || !section) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setHeroVisible(entry.isIntersecting);
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  const toggleSound = () => {
    const video = videoRef.current;
    if (!video) return;
    const next = !soundOn;
    video.muted = !next;
    if (next) video.play().catch(() => {});
    setSoundOn(next);
  };

  return (
    <header ref={sectionRef} data-testid="hero-section" className="relative h-screen w-full overflow-hidden bg-black">
      {/* HERO SHOWREEL — master: /app/assets/illenium-aftmv.mp4 (swap src to change) */}
      <div className="absolute inset-0 bg-[#0A0A0A]" data-media-slot="hero-video">
        <video
          ref={videoRef}
          data-testid="hero-video"
          src="/media/hero-reel.mp4"
          poster="/media/hero-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover brightness-[0.35]"
        />
        <div className="absolute inset-0 bg-black/40 noise" />
        <Corner className="top-24 left-6 md:left-10 border-t border-l" />
        <Corner className="top-24 right-6 md:right-10 border-t border-r" />
        <Corner className="bottom-24 left-6 md:left-10 border-b border-l" />
        <Corner className="bottom-24 right-6 md:right-10 border-b border-r" />
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: BASE_DELAY + 0.6, duration: 1 }}
        className="absolute top-6 left-6 md:top-8 md:left-10 z-10 font-jetmono text-[10px] md:text-[11px] tracking-[0.18em] md:tracking-[0.3em] uppercase text-white/80"
        data-testid="hero-wordmark"
      >
        KHUSH PATEL<span className="text-[#808080] hidden sm:inline"> — PHOTO / FILM / POST</span>
      </motion.div>

      <motion.div style={{ y, opacity }} className="relative z-10 h-full flex flex-col items-center justify-center px-6">
        <h1 data-testid="hero-heading" className="text-center font-syncopate font-bold uppercase leading-[1.02] tracking-tight text-[8.5vw] sm:text-[7vw] lg:text-[5.5vw]">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: BASE_DELAY + i * 0.13, duration: 1, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        <p className="sr-only">
          Khush Patel is a Los Angeles-based photographer and filmmaker specializing in concert and festival
          photography, fashion and portrait editorials, music video cinematography, color grading in DaVinci
          Resolve, and high-end photo retouching — available for bookings in LA and worldwide.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: BASE_DELAY + 0.9, duration: 1 }}
        className="absolute bottom-8 left-6 md:left-10 z-10 font-jetmono text-[10px] tracking-[0.2em] uppercase text-[#808080] max-w-xs"
        data-testid="hero-media-placeholder-label"
      >
        ILLENIUM — INDIA TOUR 2024<br />(AFTERMOVIE)
      </motion.div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3">
        <span className="font-jetmono text-[10px] tracking-[0.4em] uppercase text-[#808080]">SCROLL</span>
        <span className="block w-px h-10 bg-white animate-scroll-cue" />
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: BASE_DELAY + 0.9, duration: 1 }}
        className="absolute bottom-8 right-6 md:right-10 z-10 font-jetmono text-[10px] tracking-[0.2em] uppercase text-[#808080] text-right"
      >
        34.0522° N<br />118.2437° W
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: heroVisible ? 1 : 0 }}
        transition={{ duration: 0.3, delay: 0 }}
        className="fixed top-6 right-28 md:top-8 md:right-40 z-[60]"
        style={{ pointerEvents: heroVisible ? "auto" : "none" }}
      >
        <button
          data-testid="sound-toggle-btn"
          onClick={toggleSound}
          className="flex items-center gap-2 font-jetmono text-[10px] tracking-[0.3em] uppercase text-white bg-black/70 backdrop-blur-md border border-[#333] px-3.5 py-2.5 hover:bg-white hover:text-black hover:border-white transition-colors duration-300"
        >
          {soundOn ? <Volume2 size={13} /> : <VolumeX size={13} />}
          {soundOn ? "[ SOUND ON ]" : "[ SOUND OFF ]"}
        </button>
      </motion.div>
    </header>
  );
};
